import {
  Button,
  CardBackground,
  NumberField,
  Tag,
  TextField,
  Typography,
} from "@hellocarbo/lasagne";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  data,
  Form,
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useNavigation,
} from "react-router";
import invariant from "tiny-invariant";

import PageWrapper from "@/components/ui/PageWrapper";
import { TAnalysisPreview } from "@/types";

export async function loader({ params }: LoaderFunctionArgs) {
  const { analysisId } = params;
  invariant(analysisId, "No analysisId found!");

  const analysisPreviewResponse = await fetch(
    `/api/client/analysis_imports/${analysisId}/preview`
  );
  if (analysisPreviewResponse.status !== 200) {
    throw data("Failed to load this analysis preview", {
      status: analysisPreviewResponse.status,
    });
  }

  const { data: analysisPreview } = (await analysisPreviewResponse.json()) as {
    data: TAnalysisPreview;
  };

  return { analysisPreview };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { analysisId } = params;
  invariant(analysisId, "No analysis id found!");

  const formData = await request.formData();
  const quantity = Number(formData.get("quantity"));

  if (!Number.isFinite(quantity) || quantity <= 0) {
    return data({ error: "quantity must be > 0" }, { status: 400 });
  }

  const response = await fetch(
    `/api/client/analysis_imports/${analysisId}/import`,
    {
      method: "POST",
      body: JSON.stringify({
        quantity,
      }),
      headers: { "Content-Type": "application/json" },
    }
  );

  if (response.status !== 201) {
    throw data("Failed to import analysis", {
      status: response.status,
    });
  }

  const {
    data: { id },
  } = await response.json();

  return redirect(`/survey/products/${id}`);
}

function AnalysisImportPreview() {
  const { t } = useTranslation("analysisImport");
  const { analysisPreview } = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const [quantity, setQuantity] = useState(0);

  const quantityError =
    Number.isNaN(quantity) || quantity <= 0
      ? "La quantite doit etre strictement superieure a 0."
      : undefined;

  const isSubmitDisabled =
    Boolean(quantityError) || navigation.state !== "idle";

  return (
    <PageWrapper>
      <div className="my-lg grid grid-cols-4">
        <CardBackground className="col-start-2 col-end-4">
          <Typography as="h1" step="2" weight="bold" className="mb-xs">
            {t("analysisImport:title")}
          </Typography>
          <Typography
            as="p"
            className="mb-lg text-grayscale-dark whitespace-pre-line"
          >
            {t("analysisImport:description")}
          </Typography>
          <Form method="POST">
            <div className="flex flex-col">
              <TextField
                name="productName"
                label={t("analysisImport:productName")}
                defaultValue={analysisPreview.productName}
                readOnly
                fullWidth
                disabled
                className="mb-md"
              />
              <NumberField
                name="quantity"
                label={t("analysisImport:quantity.label")}
                defaultValue={quantity}
                onChange={(value) => setQuantity(value)}
                fullWidth
                required
              />
            </div>
            <div className="mt-xs">
              <Tag
                intent="warning"
                text={t("analysisImport:quantity.warning")}
              />
            </div>
            <div className="mt-lg text-center">
              <Button
                type="submit"
                isLoading={navigation.state !== "idle"}
                disabled={isSubmitDisabled}
              >
                {t("cta")}
              </Button>
            </div>
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default AnalysisImportPreview;
